import React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { ErrorModalOptions } from '~/lib/AppContext';
import ErrorModal from './ErrorModal';
import { errorModalBody } from './ErrorModalBody';

class ErrorState {
    @observable
    error: unknown = undefined;

    @observable
    options?: ErrorModalOptions = undefined;

    @observable
    visible = false;

    constructor() {
        makeObservable(this);
    }
}

const errorState = new ErrorState();

let mountedCount = 0;

// TypeScript declaration for build-time process.env substitution
declare const process: {
    env: {
        NODE_ENV: string;
    };
};

/** Shows an error modal. Only one modal is displayed at a time — a new error replaces
 *  the current one without the user needing to dismiss it first. */
export const showError = action((error: unknown, options?: ErrorModalOptions): void => {
    errorState.error = error;
    errorState.options = options;
    errorState.visible = true;
});

/** Dismisses the current error modal if one is visible. Primarily used as the modal's
 *  onDismiss handler; also exported so tests can reset the module-scoped error state. */
export const dismissError = action(() => {
    errorState.error = undefined;
    errorState.options = undefined;
    errorState.visible = false;
});

/**
 * Mount exactly one `<ErrorModalHost />` per active layout (Layout, PreLoginLayout,
 * PreSignupLayout). Because `errorState` is module-scoped, additional simultaneous mounts
 * would each render their own `<ErrorModal>` from the same state and each register a
 * `document` keydown listener — harmless functionally (dismissError is idempotent) but
 * wasteful and surprising. A new layout should reuse the existing host, not double-mount.
 */
@observer
export default class ErrorModalHost extends React.Component {
    componentDidMount() {
        mountedCount += 1;
        if (mountedCount > 1 && process.env.NODE_ENV !== 'production') {
            console.warn(
                `ErrorModalHost: ${mountedCount} instances mounted simultaneously. Mount exactly one per active layout — module-scoped errorState is shared across mounts.`
            );
        }
    }

    componentWillUnmount() {
        mountedCount -= 1;
    }

    render() {
        if (!errorState.visible) return null;
        return (
            <ErrorModal title={errorState.options?.title ?? 'Error'} onDismiss={dismissError}>
                {errorModalBody(errorState.error, errorState.options?.errorSummary)}
            </ErrorModal>
        );
    }
}
